import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { PersonnelActions } from './personnel.actions';
import { PersonnelService } from '../../services/personnel.service';
import { EncryptionService } from '../../zk/encryption.service';
import { decryptInBatches } from '../../zk/batch-decrypt.util';
import { Personnel, PersonnelWithEncryptedPii } from '../../models/personnel.model';
import { Router } from '@angular/router';

@Injectable()
export class PersonnelEffects {
  private actions$ = inject(Actions);
  private service = inject(PersonnelService);
  private encryption = inject(EncryptionService);
  private router = inject(Router);

  loadPersonnel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonnelActions.loadPersonnel),
      mergeMap(() =>
        this.service.getAll().pipe(
          mergeMap(records => from(this.decryptList(records))),
          map(personnel => PersonnelActions.loadPersonnelSuccess({ personnel })),
          catchError(err => of(PersonnelActions.loadPersonnelFailure({ error: err.message })))
        )
      )
    )
  );

  createPersonnel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonnelActions.createPersonnel),
      mergeMap(({ data }) =>
        this.service.create(data).pipe(
          mergeMap(record => from(this.decryptOne(record))),
          map(personnel => PersonnelActions.createPersonnelSuccess({ personnel })),
          catchError(err => of(PersonnelActions.createPersonnelFailure({ error: err.message })))
        )
      )
    )
  );

  createPersonnelEncrypted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonnelActions.createPersonnelEncrypted),
      mergeMap(({ data }) =>
        this.service.createEncrypted(data).pipe(
          mergeMap(record => from(this.decryptOne(record))),
          map(personnel => PersonnelActions.createPersonnelSuccess({ personnel })),
          catchError(err => of(PersonnelActions.createPersonnelFailure({ error: err.message })))
        )
      )
    )
  );

  updatePersonnel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonnelActions.updatePersonnel),
      mergeMap(({ id, data }) =>
        this.service.update(id, data).pipe(
          mergeMap(record => from(this.decryptOne(record))),
          map(personnel => PersonnelActions.updatePersonnelSuccess({ personnel })),
          catchError(err => of(PersonnelActions.updatePersonnelFailure({ error: err.message })))
        )
      )
    )
  );

  updatePersonnelEncrypted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonnelActions.updatePersonnelEncrypted),
      mergeMap(({ id, data }) =>
        this.service.updateEncrypted(id, data).pipe(
          mergeMap(record => from(this.decryptOne(record))),
          map(personnel => PersonnelActions.updatePersonnelSuccess({ personnel })),
          catchError(err => of(PersonnelActions.updatePersonnelFailure({ error: err.message })))
        )
      )
    )
  );

  deletePersonnel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonnelActions.deletePersonnel),
      mergeMap(({ id }) =>
        this.service.delete(id).pipe(
          map(() => PersonnelActions.deletePersonnelSuccess({ id })),
          catchError(err => of(PersonnelActions.deletePersonnelFailure({ error: err.message })))
        )
      )
    )
  );

  navigateAfterCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonnelActions.createPersonnelSuccess),
      tap(() => this.router.navigate(['/admin/users']))
    ),
    { dispatch: false }
  );

  navigateAfterUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonnelActions.updatePersonnelSuccess),
      tap(() => this.router.navigate(['/admin/users']))
    ),
    { dispatch: false }
  );

  assignRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonnelActions.assignRoles),
      mergeMap(({ id, data }) =>
        this.service.assignRoles(id, data).pipe(
          mergeMap(record => from(this.decryptOne(record))),
          map(personnel => PersonnelActions.assignRolesSuccess({ personnel })),
          catchError(err => of(PersonnelActions.assignRolesFailure({ error: err.message })))
        )
      )
    )
  );

  // ── Decryption helpers ────────────────────────────────────────────────────

  private async decryptList(records: PersonnelWithEncryptedPii[]): Promise<Personnel[]> {
    return decryptInBatches(records, r => this.decryptOne(r));
  }

  private async decryptOne(record: PersonnelWithEncryptedPii): Promise<Personnel> {
    if (!record.wrappedDek || Object.keys(record.encryptedFields).length === 0) {
      return this.toPlainPersonnel(record, {});
    }
    try {
      const plain = await this.encryption.decryptRecord(record.encryptedFields, record.wrappedDek);
      return this.toPlainPersonnel(record, plain);
    } catch {
      return this.toPlainPersonnel(record, {});
    }
  }

  private toPlainPersonnel(record: PersonnelWithEncryptedPii, plain: Record<string, string>): Personnel {
    return {
      id:                    record.id,
      fullName:              plain['fullName']    ?? record.fullName,
      email:                 plain['email']       ?? record.email,
      username:              record.username,
      phoneNumber:           plain['phoneNumber'] ?? record.phoneNumber,
      isActive:              record.isActive,
      isLeadTechnician:      record.isLeadTechnician,
      isAssistantTechnician: record.isAssistantTechnician,
      isStorekeeper:         record.isStorekeeper,
      preferredLanguage:     record.preferredLanguage,
      assignedCarId:         record.assignedCarId,
      assignedCarPlateNumber: record.assignedCarPlateNumber,
      roleIds:               record.roleIds,
    };
  }
}
